/**
 * Mirror backend installmentScheduleUtils for SO modification wizard display/payload.
 */

function parseScheduleDate(value) {
  if (!value) return null;
  const raw = String(value).trim().split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function dateKey(value) {
  const dt = parseScheduleDate(value);
  return dt ? dt.toISOString().slice(0, 10) : null;
}

function resolveInstallmentScheduleDate(inst) {
  if (!inst) return null;
  const direct = dateKey(inst.scheduled_invoice_date);
  if (direct) return direct;
  const match = String(inst.label || '').match(/(\d{4}-\d{2}-\d{2})/);
  return match ? dateKey(match[1]) : null;
}

export function filterInstallmentsToServicePeriod(installments, serviceFrom, serviceTo) {
  if (!installments?.length) return [];
  const fromKey = dateKey(serviceFrom);
  const toKey = dateKey(serviceTo);
  if (!fromKey || !toKey) return installments.map(i => ({ ...i }));

  return installments
    .filter(inst => {
      const instKey = resolveInstallmentScheduleDate(inst);
      if (!instKey) return false;
      return instKey >= fromKey && instKey <= toKey;
    })
    .map((inst, idx) => ({ ...inst, sequence_no: idx + 1 }));
}

/**
 * Subscription-style rows have schedule dates or TIME_PERIOD triggers.
 * Delivery/milestone lump-sum terms must not be filtered by revenue service period.
 */
export function isSubscriptionStyleBilling(installments) {
  if (!installments?.length) return false;
  return installments.some((inst) => {
    const trigger = String(inst.trigger_type || '').toUpperCase();
    if (trigger === 'TIME_PERIOD') return true;
    if (resolveInstallmentScheduleDate(inst)) return true;
    return false;
  });
}

export function normalizePercentageInstallments(installments) {
  if (!installments?.length) return [];
  const allPct = installments.every(i => i.amount_type === 'PERCENTAGE');
  if (!allPct) return installments.map(i => ({ ...i }));

  const sum = installments.reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
  if (Math.abs(sum - 100) <= 0.01 || sum <= 0) return installments.map(i => ({ ...i }));

  let running = 0;
  return installments.map((inst, idx) => {
    const isLast = idx === installments.length - 1;
    const scaled = isLast
      ? parseFloat((100 - running).toFixed(4))
      : parseFloat(((parseFloat(inst.amount_value || 0) / sum) * 100).toFixed(4));
    if (!isLast) running += scaled;
    return { ...inst, amount_value: scaled };
  });
}

/**
 * Installments shown/sent when service period is set on SO modification.
 * Mirrors backend prepareInstallmentsForSoModification: only subscription-style
 * schedules are filtered by service period; delivery/milestone rows are unchanged.
 */
export function prepareWizardInstallments(installments, serviceFrom, serviceTo) {
  if (!installments?.length) return [];
  if (!serviceFrom || !serviceTo) return installments.map(i => ({ ...i }));
  if (!isSubscriptionStyleBilling(installments)) {
    return installments.map(i => ({ ...i }));
  }
  const filtered = filterInstallmentsToServicePeriod(installments, serviceFrom, serviceTo);
  return normalizePercentageInstallments(filtered);
}
